import React, { useState, createContext, useContext, useId, useCallback } from 'react';
import { cn } from '@/lib/utils';

/* ─── Context ───────────────────────────────────────────────── */
interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
  variant: TabsVariant;
  baseId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tabs compound components must be used within <Tabs>');
  return ctx;
}

/* ─── Types ─────────────────────────────────────────────────── */
export type TabsVariant = 'default' | 'pills' | 'underline';

export interface TabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  variant?: TabsVariant;
  className?: string;
  children: React.ReactNode;
}

export interface TabsListProps {
  className?: string;
  children: React.ReactNode;
}

export interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
}

export interface TabsContentProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

/* ─── Tabs Root ─────────────────────────────────────────────── */
export function Tabs({
  defaultValue = '',
  value: controlledValue,
  onValueChange,
  variant = 'default',
  className,
  children,
}: TabsProps) {
  const [internal, setInternal] = useState(defaultValue);
  const baseId = useId();

  const currentValue = controlledValue ?? internal;

  const handleChange = useCallback(
    (v: string) => {
      if (controlledValue === undefined) setInternal(v);
      onValueChange?.(v);
    },
    [controlledValue, onValueChange]
  );

  return (
    <TabsContext.Provider
      value={{ value: currentValue, onValueChange: handleChange, variant, baseId }}
    >
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

/* ─── TabsList ──────────────────────────────────────────────── */
const listVariantStyles: Record<TabsVariant, string> = {
  default:
    'bg-gray-100 p-1 rounded-lg dark:bg-gray-800',
  pills: 'gap-2',
  underline:
    'border-b border-gray-200 dark:border-gray-700 gap-0',
};

export function TabsList({ className, children }: TabsListProps) {
  const { variant } = useTabsContext();

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const tablist = e.currentTarget;
    const tabs = Array.from(
      tablist.querySelectorAll<HTMLElement>('[role="tab"]:not([disabled])')
    );
    const idx = tabs.indexOf(e.target as HTMLElement);
    if (idx === -1) return;

    let next = -1;
    if (e.key === 'ArrowRight') {
      next = (idx + 1) % tabs.length;
    } else if (e.key === 'ArrowLeft') {
      next = (idx - 1 + tabs.length) % tabs.length;
    } else if (e.key === 'Home') {
      next = 0;
    } else if (e.key === 'End') {
      next = tabs.length - 1;
    }
    if (next >= 0) {
      e.preventDefault();
      tabs[next].focus();
      tabs[next].click();
    }
  }, []);

  return (
    <div
      role="tablist"
      onKeyDown={handleKeyDown}
      className={cn('flex items-center', listVariantStyles[variant], className)}
    >
      {children}
    </div>
  );
}

/* ─── TabsTrigger ───────────────────────────────────────────── */
const triggerBase =
  'inline-flex items-center justify-center whitespace-nowrap font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 disabled:pointer-events-none disabled:opacity-50 text-sm';

const triggerVariantStyles: Record<TabsVariant, { active: string; inactive: string }> = {
  default: {
    active:
      'bg-white text-gray-900 shadow-sm rounded-md dark:bg-gray-700 dark:text-gray-100',
    inactive:
      'text-gray-600 hover:text-gray-900 rounded-md dark:text-gray-400 dark:hover:text-gray-200',
  },
  pills: {
    active:
      'bg-primary-900 text-white rounded-full dark:bg-primary-100 dark:text-primary-900',
    inactive:
      'text-gray-600 hover:bg-gray-100 rounded-full dark:text-gray-400 dark:hover:bg-gray-800',
  },
  underline: {
    active:
      'text-primary-600 border-b-2 border-primary-600 dark:text-primary-400 dark:border-primary-400',
    inactive:
      'text-gray-600 border-b-2 border-transparent hover:text-gray-900 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200',
  },
};

export function TabsTrigger({ value, className, children, ...props }: TabsTriggerProps) {
  const { value: current, onValueChange, variant, baseId } = useTabsContext();
  const isActive = current === value;
  const styles = triggerVariantStyles[variant];

  return (
    <button
      role="tab"
      type="button"
      id={`${baseId}-trigger-${value}`}
      aria-selected={isActive}
      aria-controls={`${baseId}-content-${value}`}
      tabIndex={isActive ? 0 : -1}
      onClick={() => onValueChange(value)}
      className={cn(
        triggerBase,
        'px-3 py-2',
        isActive ? styles.active : styles.inactive,
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

/* ─── TabsContent ───────────────────────────────────────────── */
export function TabsContent({ value, className, children }: TabsContentProps) {
  const { value: current, baseId } = useTabsContext();

  if (current !== value) return null;

  return (
    <div
      role="tabpanel"
      id={`${baseId}-content-${value}`}
      aria-labelledby={`${baseId}-trigger-${value}`}
      tabIndex={0}
      className={cn('mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-lg', className)}
    >
      {children}
    </div>
  );
}

Tabs.displayName = 'Tabs';
TabsList.displayName = 'TabsList';
TabsTrigger.displayName = 'TabsTrigger';
TabsContent.displayName = 'TabsContent';
